import { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../../providers/AuthContext';
import { getAdminAuthHeaders } from '../../utils/adminApi';
import Loader from '../shared/Loader';
import { showSuccess, showError } from '../../utils/sweetAlert';
import { getDisplayImageUrl } from '../../utils/imageUtils';

const Orders = () => {
  const { token } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [sendingEmailOrderId, setSendingEmailOrderId] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    enEspera: 0,
    enPreparacion: 0,
    listoParaRetiro: 0,
    enviados: 0,
    entregados: 0,
    cancelados: 0,
    totalRevenue: 0
  });

  useEffect(() => {    
    const fetchOrders = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/orders`, {
          headers: getAdminAuthHeaders(token)
        });
        const ordersWithStatus = response.data.map(order => ({
          ...order,
          status: order.status || 'en espera'
        }));
        setOrders(ordersWithStatus);
        calculateStats(ordersWithStatus);
      } catch (err) {
        setError('Error al obtener los pedidos');
        showError('No se pudieron cargar los pedidos');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [token]);

  const calculateStats = (ordersList) => {
    const newStats = {
      total: ordersList.length,
      enEspera: ordersList.filter(o => o.status === 'en espera').length,
      enPreparacion: ordersList.filter(o => o.status === 'en preparacion').length,
      listoParaRetiro: ordersList.filter(o => o.status === 'terminado').length,
      enviados: ordersList.filter(o => o.status === 'enviado').length,
      entregados: ordersList.filter(o => o.status === 'entregado').length,
      cancelados: ordersList.filter(o => o.status === 'cancelado').length,
      totalRevenue: ordersList
        .filter(o => o.payment?.status === 'approved')
        .reduce((sum, o) => sum + (o.total || 0), 0)
    };
    setStats(newStats);
  };

  const handleStatusChange = useCallback(async (orderId, newStatus) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/orders/${orderId}`,
        { status: newStatus },
        { headers: getAdminAuthHeaders(token) }
      );
      
      const updatedOrders = orders.map(order =>
        order._id === orderId ? { ...order, status: newStatus } : order
      );
      setOrders(updatedOrders);
      calculateStats(updatedOrders);
      showSuccess('Estado actualizado correctamente');
    } catch (err) {
      showError('Error al actualizar el estado');
    }
  }, [orders, token]);

  const handleTogglePayment = useCallback(async (order) => {
    const currentStatus = order.payment?.status;
    const newStatus = currentStatus === 'approved' ? 'pending' : 'approved';
    const update = {
      'payment.status': newStatus,
      ...(newStatus === 'approved' ? { 'payment.paidAt': new Date().toISOString() } : { 'payment.paidAt': null })
    };
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/orders/${order._id}`,
        update,
        { headers: getAdminAuthHeaders(token) }
      );
      const updatedOrders = orders.map(o =>
        o._id === order._id
          ? { ...o, payment: { ...o.payment, status: newStatus, paidAt: newStatus === 'approved' ? new Date().toISOString() : null } }
          : o
      );
      setOrders(updatedOrders);
      calculateStats(updatedOrders);
      showSuccess(newStatus === 'approved' ? 'Pago marcado como aprobado' : 'Pago marcado como pendiente');
    } catch (err) {
      showError('Error al actualizar el estado de pago');
    }
  }, [orders, token]);

  const handleSendConfirmationEmail = useCallback(async (order) => {
    if (!order?._id) return;

    try {
      setSendingEmailOrderId(order._id);
      const eventType = order.status === 'enviado'
        ? 'shipped'
        : order.status === 'terminado'
          ? 'ready_for_pickup'
          : order.payment?.status === 'approved'
            ? 'payment_confirmed'
            : 'order_received';
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/email/send-order-confirmation/${order._id}`,
        { eventType },
        { headers: getAdminAuthHeaders(token) }
      );

      if (response.data?.success) {
        showSuccess(`Confirmación enviada a ${order.buyerInfo?.mail || 'la clienta'}`);
      } else {
        showError(response.data?.error || 'No se pudo enviar el email');
      }
    } catch (err) {
      showError(err.response?.data?.error || 'Error al enviar la confirmación');
    } finally {
      setSendingEmailOrderId(null);
    }
  }, [token]);

  // Función para asignar un color de fondo según el estado
  const getBackgroundColor = (status) => {
    switch(status) {
      case 'en espera':
        return '#f8f9fa'; // gris claro
      case 'en preparacion':
        return '#fff3cd'; // amarillo claro
      case 'terminado':
        return '#d4edda'; // verde claro
      case 'enviado':
        return '#cce5ff'; // azul claro
      case 'entregado':
        return '#d1e7dd'; // verde más oscuro
      case 'cancelado':
        return '#f8d7da'; // rojo claro
      default:
        return '#ffffff';
    }
  };

  // Función para obtener el icono del tipo de envío
  const getShippingIcon = (shippingType) => {
    switch(shippingType) {
      case 'dac_montevideo':
        return 'fa-motorcycle';
      case 'retiro_local':
        return 'fa-store';
      case 'envio_interior':
        return 'fa-truck';
      default:
        return 'fa-box';
    }
  };

  // Función para obtener el badge del tipo de envío
  const getShippingBadge = (shipping) => {
    if (!shipping) return null;
    
    const colors = {
      'dac_montevideo': 'bg-info',
      'retiro_local': 'bg-success',
      'envio_interior': 'bg-warning text-dark'
    };
    
    return (
      <span className={`badge ${colors[shipping.type] || 'bg-secondary'}`}>
        <i className={`fa-solid ${getShippingIcon(shipping.type)} me-1`}></i>
        {shipping.label}
        {shipping.cost > 0 && ` ($${shipping.cost})`}
      </span>
    );
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/orders`, {
        headers: getAdminAuthHeaders(token)
      });
      const ordersWithStatus = response.data.map(order => ({
        ...order,
        status: order.status || 'en espera'
      }));
      setOrders(ordersWithStatus);
      calculateStats(ordersWithStatus);
    } catch (err) {
      showError('No se pudieron cargar los pedidos');
    } finally {
      setLoading(false);
    }
  };

  // Filtrar pedidos por fecha, estado y búsqueda
  const filteredOrders = useMemo(() => orders.filter(order => {
    const orderDate = new Date(order.date);
    const now = new Date();

    // Date filter
    let matchesDate = true;
    if (dateFilter === 'today') {
      matchesDate = orderDate.toDateString() === now.toDateString();
    } else if (dateFilter === 'week') {
      const weekAgo = new Date(now);
      weekAgo.setDate(weekAgo.getDate() - 7);
      matchesDate = orderDate >= weekAgo;
    } else if (dateFilter === 'month') {
      const monthAgo = new Date(now);
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      matchesDate = orderDate >= monthAgo;
    } else if (dateFilter === 'custom') {
      if (dateFrom) {
        const from = new Date(dateFrom + 'T00:00:00');
        matchesDate = orderDate >= from;
      }
      if (dateTo && matchesDate) {
        const to = new Date(dateTo + 'T23:59:59');
        matchesDate = orderDate <= to;
      }
    }

    // Status filter
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;

    // Search filter
    let matchesSearch = true;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      matchesSearch = 
        String(order.orderNumber || '').toLowerCase().includes(q) ||
        (order.buyerInfo?.nombre || '').toLowerCase().includes(q) ||
        (order.buyerInfo?.mail || '').toLowerCase().includes(q);
    }

    return matchesDate && matchesStatus && matchesSearch;
  }), [orders, dateFilter, dateFrom, dateTo, statusFilter, searchQuery]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      'en espera': { color: '#6c757d', icon: 'fa-clock', text: 'En Espera' },
      'en preparacion': { color: '#ffc107', icon: 'fa-box-open', text: 'Preparando' },
      'terminado': { color: '#1f8e38', icon: 'fa-check', text: 'Listo' },
      'enviado': { color: '#17a2b8', icon: 'fa-truck', text: 'Enviado' },
      'entregado': { color: '#1f8e38', icon: 'fa-check-double', text: 'Entregado' },
      'cancelado': { color: '#dc3545', icon: 'fa-times', text: 'Cancelado' }
    };
    
    const config = statusConfig[status] || statusConfig['en espera'];
    
    return (
      <span className="badge" style={{ backgroundColor: config.color, fontSize: '0.85rem', padding: '6px 12px' }}>
        <i className={`fa-solid ${config.icon} me-1`}></i>{config.text}
      </span>
    );
  };

  const getEmailNotificationMeta = (order) => {
    const notifications = order.notifications || {};
    const customerEvents = [
      { label: notifications.fulfillmentEmailType === 'ready_for_pickup' ? 'Retiro avisado' : 'Envío avisado', sentAt: notifications.fulfillmentEmailSentAt, error: notifications.fulfillmentEmailError },
      { label: 'Compra confirmada', sentAt: notifications.orderConfirmationEmailSentAt, error: notifications.orderConfirmationEmailError },
      { label: 'Pedido recibido', sentAt: notifications.orderReceivedEmailSentAt, error: notifications.orderReceivedEmailError }
    ];
    const sent = customerEvents.find(event => event.sentAt);

    if (sent) {
      return {
        label: sent.label,
        icon: 'fa-envelope-circle-check',
        background: '#e8f7ee',
        color: '#1f8e38',
        detail: new Date(sent.sentAt).toLocaleString('es-UY', {
          dateStyle: 'short',
          timeStyle: 'short'
        })
      };
    }

    const failed = customerEvents.find(event => event.error);
    if (failed) {
      return {
        label: 'Email con error',
        icon: 'fa-envelope-circle-xmark',
        background: '#fdecec',
        color: '#c62828',
        detail: failed.error
      };
    }

    return {
      label: 'Email pendiente',
      icon: 'fa-envelope',
      background: '#f3f4f6',
      color: '#6b7280',
      detail: 'Todavía no se registró un envío al cliente'
    };
  };

  const getSectionLabel = (text, extra = null) => (
    <div style={{ borderLeft: '3px solid var(--brand-primary, #1a1a1a)', paddingLeft: 8, marginBottom: 10, marginTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontSize: '0.63rem', fontWeight: 700, letterSpacing: '0.13em', textTransform: 'uppercase', color: 'var(--text-muted, #888)' }}>{text}</span>
      {extra && <span style={{ fontSize: '0.68rem', color: 'var(--text-muted, #999)' }}>{extra}</span>}
    </div>
  );

  const getPaymentMethodLabel = (method) => {
    const map = { efectivo: 'Efectivo en local', transferencia: 'Transferencia (BROU / Prex)', mercadopago_debito: 'Link de MercadoPago Débito', mercadopago_credito: 'Link de MercadoPago Crédito', mercadopago: 'MercadoPago', reserva: 'Reserva / Pago manual' };
    return map[method] || method || '—';
  };

  const getPaymentMethodIcon = (method) => {
    const map = { efectivo: 'fa-money-bill-wave', transferencia: 'fa-building-columns', mercadopago_debito: 'fa-credit-card', mercadopago_credito: 'fa-credit-card', mercadopago: 'fa-credit-card', reserva: 'fa-clock' };
    return map[method] || 'fa-wallet';
  };

  if (loading) return <Loader />;
  if (error) return <p className="text-center mt-5 text-danger">{error}</p>;

  const getOrderStatusBadgeClass = (status) => {
    const map = {
      'en espera': 'badge-secondary',
      'en preparacion': 'badge-warning',
      'terminado': 'badge-success',
      'enviado': 'badge-info',
      'entregado': 'badge-success',
      'cancelado': 'badge-danger'
    };
    return map[status] || 'badge-secondary';
  };

  const getOrderStatusText = (status) => {
    const map = {
      'en espera': 'En Espera',
      'en preparacion': 'En Preparación',
      'terminado': 'Listo para Retiro',
      'enviado': 'Enviado',
      'entregado': 'Entregado',
      'cancelado': 'Cancelado'
    };
    return map[status] || status;
  };

  const statusPills = [
    { key: 'all', label: '', count: orders.length, dot: null },
    { key: 'en espera', label: 'En Espera', count: stats.enEspera, dot: '#6c757d' },
    { key: 'en preparacion', label: 'En Preparación', count: stats.enPreparacion, dot: '#ffc107' },
    { key: 'terminado', label: 'Listo para Retiro', count: stats.listoParaRetiro, dot: '#1f8e38' },
    { key: 'enviado', label: 'Enviado', count: stats.enviados, dot: '#17a2b8' },
    { key: 'entregado', label: 'Entregado', count: stats.entregados, dot: '#1f8e38' },
    { key: 'cancelado', label: 'Cancelado', count: stats.cancelados, dot: '#dc3545' }
  ];

  return (
    <div className="orders-page">
      {/* Header */}
      <div className="admin-page-header">
        <div className="header-left">
          <h1>Pedidos Realizados</h1>
          <p>Gestiona y visualiza todos los pedidos de tu tienda</p>
        </div>
        <div className="header-right">
          <button className="btn btn-dark" onClick={handleRefresh} disabled={loading}>
            <i className="fas fa-rotate-right me-2"></i> Actualizar
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="admin-stat-row">
        <div className="admin-stat-card">
          <div className="admin-stat-icon blue">
            <i className="fas fa-shopping-cart"></i>
          </div>
          <div className="admin-stat-info">
            <span className="stat-number">{stats.total}</span>
            <span className="stat-label">Total Pedidos</span>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon orange">
            <i className="fas fa-clock"></i>
          </div>
          <div className="admin-stat-info">
            <span className="stat-number">{stats.enEspera}</span>
            <span className="stat-label">Pendientes</span>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon red">
            <i className="fas fa-box-open"></i>
          </div>
          <div className="admin-stat-info">
            <span className="stat-number">{stats.enPreparacion}</span>
            <span className="stat-label">En Preparación</span>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon green">
            <i className="fas fa-dollar-sign"></i>
          </div>
          <div className="admin-stat-info">
            <span className="stat-number">${stats.totalRevenue.toLocaleString('es-AR')}</span>
            <span className="stat-label">Ingresos (Pagados)</span>
          </div>
        </div>
      </div>

      {/* Filter Bar: Search + Date Buttons + Date Range */}
      <div className="admin-filter-bar" style={{ flexWrap: 'wrap' }}>
        <div className="filter-search">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Buscar por N° pedido, nombre, email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="admin-date-filters">
          <button className={`date-filter-btn ${dateFilter === 'today' ? 'active' : ''}`} onClick={() => setDateFilter('today')}>Hoy</button>
          <button className={`date-filter-btn ${dateFilter === 'week' ? 'active' : ''}`} onClick={() => setDateFilter('week')}>Última Semana</button>
          <button className={`date-filter-btn ${dateFilter === 'month' ? 'active' : ''}`} onClick={() => setDateFilter('month')}>Último Mes</button>
          <button className={`date-filter-btn ${dateFilter === 'all' ? 'active' : ''}`} onClick={() => setDateFilter('all')}>Todos</button>
          <input
            type="date"
            className="admin-date-input"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setDateFilter('custom'); }}
          />
          <span className="date-range-separator">—</span>
          <input
            type="date"
            className="admin-date-input"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setDateFilter('custom'); }}
          />
        </div>
      </div>

      {/* Status Pills */}
      <div className="admin-status-pills">
        {statusPills.map(pill => (
          <button
            key={pill.key}
            className={`status-pill ${statusFilter === pill.key ? 'active' : ''}`}
            onClick={() => setStatusFilter(pill.key)}
          >
            {pill.dot && <span className="pill-dot" style={{ background: pill.dot }}></span>}
            {pill.key === 'all' ? '' : pill.label}
            <span className="pill-count">{pill.count}</span>
          </button>
        ))}
      </div>

      {/* Order count */}
      <div style={{ fontSize: '0.85rem', color: '#9ca3af', marginBottom: '16px' }}>
        {filteredOrders.length} pedido{filteredOrders.length !== 1 ? 's' : ''}
      </div>

      {/* Orders List */}
	      {filteredOrders.length === 0 ? (
        <div className="admin-empty-state">
          <div className="empty-icon">
            <i className="fas fa-inbox"></i>
          </div>
          <h4>No hay pedidos</h4>
          <p>No se encontraron pedidos con los filtros actuales</p>
        </div>
      ) : (
        <div>
	          {filteredOrders.map(order => {
              const emailMeta = getEmailNotificationMeta(order);

            return (
	            <div key={order._id} className="admin-order-card">
	              <div className="order-main" onClick={() => setExpandedOrder(expandedOrder === order._id ? null : order._id)}>
	                <div className="order-left">
	                  <span className="order-number">#{order.orderNumber || '—'}</span>
	                  <div className="order-badges">
                    <span className={`order-badge ${getOrderStatusBadgeClass(order.status)}`}>
                      {getOrderStatusText(order.status)}
                    </span>
	                    {order.payment?.status === 'approved' ? (
	                      <span className="order-badge badge-paid">
	                        <i className="fas fa-check me-1"></i>Pagado
	                      </span>
                    ) : (
                      <span className="order-badge badge-unpaid">
	                        <i className="fas fa-times me-1"></i>Pago Pendiente
	                      </span>
	                    )}
                      {emailMeta.label !== 'Email pendiente' && (
                        <span
                          className="order-badge"
                          style={{
                            backgroundColor: emailMeta.background,
                            color: emailMeta.color,
                            border: `1px solid ${emailMeta.color}22`
                          }}
                          title={emailMeta.detail}
                        >
                          <i className={`fas ${emailMeta.icon} me-1`}></i>{emailMeta.label}
                        </span>
                      )}
	                  </div>
	                  <div className="order-meta">
                    <span><i className="fas fa-calendar"></i>{new Date(order.date).toLocaleDateString('es-UY')}</span>
                    <span><i className="fas fa-clock"></i>{new Date(order.date).toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit' })}</span>
                    {order.buyerInfo?.nombre && (
                      <span><i className="fas fa-user"></i>{order.buyerInfo.nombre}</span>
                    )}
                  </div>
                </div>
                <div className="order-right">
                  <span className="order-amount">${order.total}</span>
                  <button className="order-expand" onClick={(e) => { e.stopPropagation(); setExpandedOrder(expandedOrder === order._id ? null : order._id); }}>
                    <i className={`fas fa-chevron-${expandedOrder === order._id ? 'up' : 'down'}`}></i>
                  </button>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedOrder === order._id && (
                <div className="order-details">
                  <div className="row">

                    {/* Col 1 — Productos */}
                    <div className="col-md-4">
                      {getSectionLabel('PRODUCTOS', `${order.items.length} item${order.items.length !== 1 ? 's' : ''}`)}
                      {order.items.map((item, idx) => {
                        const imgSrc = item.variant?.images?.[0] || item.images?.[0] || item.image;
                        return (
                          <div key={idx} className="mb-2 p-2" style={{ backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                            <div className="d-flex gap-2">
                              {imgSrc ? (
                                <img src={getDisplayImageUrl(imgSrc)} alt={item.name}
                                  style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 4, flexShrink: 0, border: '1px solid #e0e0e0' }}
                                />
                              ) : (
                                <div style={{ width: 44, height: 44, background: '#e9ecef', borderRadius: 4, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <i className="fas fa-shirt" style={{ color: '#adb5bd', fontSize: '1rem' }}></i>
                                </div>
                              )}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div className="d-flex justify-content-between">
                                  <span style={{ fontSize: '0.83rem', fontWeight: 500 }}>
                                    <strong>{item.quantity}×</strong> {item.name}
                                  </span>
                                  <strong style={{ fontSize: '0.83rem', flexShrink: 0 }}>${item.price * item.quantity}</strong>
                                </div>
                                {item.variant && (item.variant.size || item.variant.color) && (
                                  <div className="mt-1">
                                    {item.variant.size && <span className="badge bg-secondary me-1" style={{ fontSize: '0.68rem' }}>{item.variant.size}</span>}
                                    {item.variant.color && <span className="badge bg-secondary me-1" style={{ fontSize: '0.68rem' }}>{item.variant.color}</span>}
                                    {item.variant.sku && <div className="text-muted" style={{ fontSize: '0.68rem' }}>SKU: {item.variant.sku}</div>}
                                  </div>
                                )}
                                {!item.variant?.size && !item.variant?.color && item.variantInfo && (
                                  <span className="badge bg-secondary" style={{ fontSize: '0.68rem' }}>{item.variantInfo}</span>
                                )}
                                {item.selectedSizes?.length > 0 && (
                                  <div className="mt-1">
                                    {item.selectedSizes.map((s, i) => <span key={i} className="badge bg-secondary me-1" style={{ fontSize: '0.68rem' }}>{s}</span>)}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div className="mt-2" style={{ fontSize: '0.83rem', borderTop: '1px solid #eee', paddingTop: 8 }}>
                        <div className="d-flex justify-content-between text-muted mb-1">
                          <span>Subtotal</span><span>${order.subtotal || order.total}</span>
                        </div>
                        <div className="d-flex justify-content-between text-muted mb-1">
                          <span>Envío</span>
                          <span style={{ color: (order.shipping?.cost || 0) === 0 ? '#27ae60' : 'inherit' }}>
                            {(order.shipping?.cost || 0) === 0 ? 'Gratis' : `$${order.shipping.cost}`}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between fw-bold">
                          <span>Total</span><span>${order.total}</span>
                        </div>
                      </div>
                    </div>

                    {/* Col 2 — Cliente, Envío, Forma de Pago */}
                    <div className="col-md-4">
                      {getSectionLabel('CLIENTE')}
                      {order.buyerInfo && (
                        <div className="mb-1" style={{ fontSize: '0.83rem' }}>
                          <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 3 }}>{order.buyerInfo.nombre}</div>
                          {order.buyerInfo.mail && <div className="text-muted"><i className="fas fa-at me-1" style={{ fontSize: '0.72rem' }}></i>{order.buyerInfo.mail}</div>}
                          {order.buyerInfo.cedula && <div className="text-muted"><i className="fas fa-id-card me-1" style={{ fontSize: '0.72rem' }}></i>{order.buyerInfo.cedula}</div>}
                          {order.buyerInfo.telefono && <div className="text-muted"><i className="fas fa-phone me-1" style={{ fontSize: '0.72rem' }}></i>{order.buyerInfo.telefono}</div>}
                        </div>
                      )}
                      {getSectionLabel('ENVÍO')}
                      <div className="mb-1" style={{ fontSize: '0.83rem' }}>
                        <div className="d-flex align-items-center gap-2">
                          <i className={`fas ${getShippingIcon(order.shipping?.type)} text-muted`} style={{ fontSize: '0.85rem', width: 14 }}></i>
                          <span>{order.shipping?.label || '—'}</span>
                        </div>
                        {order.shippingAddress && order.shipping?.type !== 'retiro_local' && (
                          <div className="text-muted mt-1" style={{ fontSize: '0.78rem', paddingLeft: 20 }}>
                            {order.shippingAddress.direccion}, {order.shippingAddress.ciudad}
                            {order.shippingAddress.departamento && `, ${order.shippingAddress.departamento}`}
                          </div>
                        )}
                        {order.shipping?.type === 'retiro_local' && order.shippingAddress?.ciudad && order.shippingAddress.ciudad !== 'Montevideo' && (
                          <div className="text-muted mt-1" style={{ fontSize: '0.78rem', paddingLeft: 20 }}>
                            {order.shippingAddress.ciudad}
                          </div>
                        )}
                      </div>
                      {getSectionLabel('FORMA DE PAGO')}
                      <div className="d-flex align-items-center gap-2 mb-1" style={{ fontSize: '0.83rem' }}>
                        <i className={`fas ${getPaymentMethodIcon(order.payment?.method)} text-muted`} style={{ fontSize: '0.85rem', width: 14 }}></i>
                        <span>{getPaymentMethodLabel(order.payment?.method)}</span>
                      </div>
                      {order.payment?.paidAt && (
                        <div className="text-muted" style={{ fontSize: '0.75rem', paddingLeft: 20 }}>
                          <i className="fas fa-calendar-check me-1"></i>
                          Pagado el {new Date(order.payment.paidAt).toLocaleString('es-UY', { dateStyle: 'short', timeStyle: 'short' })}
                        </div>
                      )}
                      {order.notes && (
                        <div className="mt-2 p-2" style={{ backgroundColor: '#fff3cd', borderRadius: '6px', fontSize: '0.8rem' }}>
                          <i className="fas fa-comment me-1"></i>{order.notes}
                        </div>
                      )}
                    </div>

                    {/* Col 3 — Estado, Pago, Email */}
                    <div className="col-md-4">
                      {getSectionLabel('ESTADO DEL PEDIDO')}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 4 }}>
                        {['en espera', 'en preparacion', 'terminado', 'enviado', 'entregado', 'cancelado'].map(st => (
                          <button
                            key={st}
                            onClick={() => handleStatusChange(order._id, st)}
                            style={{
                              fontSize: '0.72rem',
                              padding: '7px 4px',
                              border: `1px solid ${order.status === st ? (st === 'cancelado' ? '#dc3545' : 'var(--brand-primary, #1a1a1a)') : '#dee2e6'}`,
                              background: order.status === st ? (st === 'cancelado' ? '#dc3545' : 'var(--brand-primary, #1a1a1a)') : '#fff',
                              color: order.status === st ? '#fff' : '#495057',
                              borderRadius: 4,
                              cursor: 'pointer',
                              fontFamily: 'var(--font-body, Inter, sans-serif)',
                              fontWeight: order.status === st ? 600 : 400,
                              transition: 'all 0.15s'
                            }}
                          >
                            {getOrderStatusText(st)}
                          </button>
                        ))}
                      </div>
                      {getSectionLabel('PAGO')}
                      <button
                        onClick={() => handleTogglePayment(order)}
                        style={{
                          width: '100%', padding: '9px 12px', fontSize: '0.78rem', fontWeight: 600,
                          border: `1px solid ${order.payment?.status === 'approved' ? '#dc3545' : '#27ae60'}`,
                          background: order.payment?.status === 'approved' ? 'transparent' : '#edfaf3',
                          color: order.payment?.status === 'approved' ? '#dc3545' : '#27ae60',
                          borderRadius: 4, cursor: 'pointer', display: 'flex',
                          alignItems: 'center', justifyContent: 'center', gap: 6,
                          fontFamily: 'var(--font-body, Inter, sans-serif)', transition: 'all 0.15s',
                          marginBottom: 4
                        }}
                      >
                        {order.payment?.status === 'approved'
                          ? <><i className="fas fa-times"></i> Marcar como Pendiente</>
                          : <><i className="fas fa-check"></i> Marcar como Pagado</>}
                      </button>
                      {getSectionLabel('CONFIRMACIÓN EMAIL')}
                      <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                        <button
                          onClick={() => handleSendConfirmationEmail(order)}
                          disabled={sendingEmailOrderId === order._id || !order.buyerInfo?.mail}
                          style={{
                            fontSize: '0.75rem', padding: '6px 12px',
                            border: '1px solid #dee2e6', background: '#fff', borderRadius: 4,
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                            fontFamily: 'var(--font-body, Inter, sans-serif)',
                            opacity: !order.buyerInfo?.mail ? 0.5 : 1
                          }}
                        >
                          {sendingEmailOrderId === order._id
                            ? <><i className="fas fa-spinner fa-spin"></i> Enviando...</>
                            : <><i className="fas fa-paper-plane"></i> Reenviar</>}
                        </button>
                        {getEmailNotificationMeta(order).label !== 'Email pendiente' && (
                          <span style={{ fontSize: '0.72rem', padding: '4px 8px', background: getEmailNotificationMeta(order).background, color: getEmailNotificationMeta(order).color, border: '1px solid #c3e6cb', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <i className={`fas ${getEmailNotificationMeta(order).icon}`}></i> {getEmailNotificationMeta(order).label}
                          </span>
                        )}
                      </div>
                      <p className="text-muted mb-0" style={{ fontSize: '0.72rem' }}>
                        Los avisos se envían al crear el pedido y al marcarlo como enviado o listo para retirar.
                      </p>
                    </div>

                  </div>
                </div>
              )}
	            </div>
	          );
            })}
	        </div>
	      )}
	    </div>
	  );
};

export default Orders;
